import AddEntity from "components/Entity/addMoreEntity";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import ToolTip from "components/design/toolTip";
import { EditGroupName } from "components/Entity/EditGroupName";
import { EditPencil } from "components/icons/EditPencil";
import { PlusIcon } from "components/icons/PlusIcon";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useCreateEntityMutation } from "store/apis/group";
import { FlipDirection, OverviewTreeNode } from "./brandOverview.types";
import { CompanyOverviewPanel } from "./CompanyOverviewPanel";
import { EntityOverviewPanel } from "./EntityOverviewPanel";
import { EntityTree } from "./EntityTree";
import { FlipPanel } from "./FlipPanel";
import { useBrandOverviewData } from "./useBrandOverviewData";

type AddEntityForm = {
  company_name: string;
  formation_type_id: string;
  country: string;
  state_id: string;
};

const COMPANY_PANEL_KEY = "company";

// pre-order index of every node, used to stagger the card load animation
const buildOrderIndex = (tree: OverviewTreeNode[]): Map<string, number> => {
  const orderIndex = new Map<string, number>();
  let counter = 0;

  const walk = (nodes: OverviewTreeNode[]) => {
    nodes.forEach((node) => {
      orderIndex.set(node.entity.id, counter);
      counter += 1;
      walk(node.children);
    });
  };

  walk(tree);
  return orderIndex;
};

export const BrandOverview = () => {
  const { company, entities, tree, isLoading, isError, isSuccess, refetch } =
    useBrandOverviewData();
  const { alertToast } = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();
  const editGroupName = useModal();
  const { isMobile } = useWindowSize();

  const [createEntity, { isLoading: isCreating }] = useCreateEntityMutation();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [direction, setDirection] = useState<FlipDirection>("forward");
  const [showAddEntity, setShowAddEntity] = useState(false);

  const orderIndex = useMemo(() => buildOrderIndex(tree), [tree]);

  const selectedEntity = entities.find((entity) => entity.id === selectedId);
  const isSingle = entities.length === 1;

  const goToEntityDetails = (entityId: string) => {
    history.push(`${url}/entity/${entityId}`);
  };

  // Mobile has no room for the overview panel, so a card opens the entity
  // details page directly instead of flipping the panel.
  const handleSelect = (entityId: string) => {
    if (isMobile) {
      goToEntityDetails(entityId);
      return;
    }

    setDirection("forward");
    setSelectedId(entityId);
  };

  const handleClose = () => {
    setDirection("reverse");
    setSelectedId(null);
  };

  const handleCompanyViewMissing = () => {
    const targetId = tree[0]?.entity.id ?? entities[0]?.id;
    if (targetId) goToEntityDetails(targetId);
  };

  const handleAddEntity = () => setShowAddEntity(true);

  const addEntityData = async (form: AddEntityForm) => {
    try {
      await createEntity({
        groupId,
        payload: {
          company_name: form.company_name,
          formation_type_id: form.formation_type_id,
          country_id: form.country,
          state_id: form.state_id,
        },
      }).unwrap();

      setShowAddEntity(false);
      refetch();
    } catch (err) {
      alertToast({
        message:
          (err as { data?: { message?: string } })?.data?.message ??
          "Something went wrong",
      });
    }
  };

  const isEmpty = !company || entities.length === 0;

  const panelKey = selectedEntity ? selectedEntity.id : COMPANY_PANEL_KEY;

  return (
    <>
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isError={isError}
        isEmpty={isEmpty}
      >
        <Async.Empty>
          <div className="t-flex t-flex-col t-gap-2 t-rounded-xl t-border t-border-solid t-border-neutral-10 t-p-6 t-text-body t-text-neutral-40">
            No entities to show yet.
          </div>
        </Async.Empty>
        <Async.ErrorHandler>
          <div className="t-flex t-flex-col t-gap-2 t-rounded-xl t-border t-border-solid t-border-neutral-10 t-p-6 t-text-body t-text-neutral-40">
            Something went wrong loading your entities.
          </div>
        </Async.ErrorHandler>
        <Async.Success>
          <div className="t-flex t-flex-col t-gap-4">
            <div className="t-sticky t-top-0 t-z-10 t-flex t-gap-9 t-bg-surface t-py-2">
              <div className="t-flex t-min-w-0 t-flex-[5] t-items-center t-justify-between t-gap-4">
                <div className="t-flex t-min-w-0 t-items-center t-gap-2.5">
                  <span className="t-truncate t-text-title-h3 t-text-text-60">
                    {company?.name}
                  </span>
                  {isAdmin && (
                    <ToolTip text="Edit Brand name" side="bottom">
                      <Button
                        customType="ghost_icon"
                        size="small"
                        onClick={editGroupName.open}
                      >
                        <EditPencil />
                      </Button>
                    </ToolTip>
                  )}
                </div>
                {!isSingle && (
                  <Button
                    customType="link_neutral"
                    onClick={handleAddEntity}
                    leftIcon={<PlusIcon size="14" />}
                  >
                    Add entity
                  </Button>
                )}
              </div>
              <div className="t-flex-[4] sm:t-hidden" />
            </div>

            <div className="t-flex t-items-start t-gap-9">
              <div className="t-min-w-0 t-flex-[5]">
                <EntityTree
                  tree={tree}
                  selectedId={selectedId}
                  orderIndex={orderIndex}
                  showAddCard={isSingle}
                  addLabel="Add another entity"
                  onSelect={handleSelect}
                  onAddEntity={handleAddEntity}
                />
              </div>

              <div className="t-sticky t-top-12 t-min-w-0 t-flex-[4] t-self-start sm:t-hidden">
                <FlipPanel panelKey={panelKey} direction={direction}>
                  {selectedEntity ? (
                    <EntityOverviewPanel
                      entity={selectedEntity}
                      onClose={handleClose}
                      onViewDetails={goToEntityDetails}
                    />
                  ) : (
                    company && (
                      <CompanyOverviewPanel
                        company={company}
                        onViewMissing={handleCompanyViewMissing}
                      />
                    )
                  )}
                </FlipPanel>
              </div>
            </div>
          </div>
        </Async.Success>
      </Async.Root>

      <AddEntity
        addEntityData={addEntityData}
        showAddEntity={showAddEntity}
        setShowAddEntity={setShowAddEntity}
        isCreating={isCreating}
      />

      {isAdmin && (
        <EditGroupName
          {...editGroupName}
          groupId={groupId}
          groupName={company?.name || ""}
        />
      )}
    </>
  );
};
